import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from 'react-query';
import { RadioGroup } from '@base-ui/react/radio-group';
import type { AccessActionsDTO, AccessReportRowDTO } from '@webip/shared/src/dto/access_report';
import type { ArpDiscoveredDeviceDTO, DeviceGroupRefDTO, NetworkLanDeviceDTO } from '@webip/shared/src/dto/appliance';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import AlertBox from '@/components/layout/AlertBox';
import CampoRadio from '@/components/Formulario/CampoRadio';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import { FormLanDevice } from '@/dashboard/FormLanDevice';
import { toastError, toastSuccess, toastWarning } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import { opcoesAlvo } from './acoes';

const AVISO_SYNC = 'Sincronize a appliance para aplicar no equipamento.';

/** Opções da linha (dispositivo, grupos, políticas) — mesma consulta para os dois modais. */
function useOpcoes(row: AccessReportRowDTO | null) {
    return useQuery(
        ['access_log_acoes', row?.applianceId, row?.originIp, row?.type],
        async () => {
            const params = new URLSearchParams({
                applianceId: String(row!.applianceId),
                srcIp: row!.originIp!,
                tipo: row!.type === 'proxy' ? 'proxy' : 'firewall',
            });
            const res = await axios.get<{ ok: boolean; result: AccessActionsDTO }>(
                `/api/report/access-logs/acoes?${params}`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        { enabled: !!row?.applianceId && !!row?.originIp, staleTime: 0 },
    );
}

function Carregando({ q }: { q: ReturnType<typeof useOpcoes> }) {
    if (q.isLoading) return <span style={{ opacity: .7 }}>Carregando…</span>;
    if (q.isError) return <AlertBox variant="danger">{extractApiError(q.error, 'Erro ao carregar as opções.').message}</AlertBox>;
    return null;
}

// ─── Nova regra ───────────────────────────────────────────────────────────────

export function RegraDaLinhaModal({ row, https, onClose }: {
    row: AccessReportRowDTO | null;
    /** A appliance tem matcher de SNI (MikroTik). */
    https: boolean;
    onClose: () => void;
}) {
    const q = useOpcoes(row);
    const alvos = useMemo(() => (row ? opcoesAlvo(row, { https }) : []), [row, https]);
    const [alvoIdx, setAlvoIdx] = useState('0');
    const [acao, setAcao] = useState<'allow' | 'block'>('allow');
    const [policyId, setPolicyId] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const politicas = q.data?.policies ?? [];

    // Padrões ao abrir: o inverso do que aconteceu, na política que decidiu
    // (ou na primeira ativa que cobre a origem).
    useEffect(() => {
        if (!row) return;
        setAlvoIdx('0');
        setAcao(row.action === 'bloqueado' ? 'allow' : 'block');
    }, [row]);
    useEffect(() => {
        if (!row || !q.data) return;
        const decidiu = q.data.policies.find((p) => p.id === row.policyId);
        const cobre = q.data.policies.find((p) => p.enabled && p.coversOrigin);
        setPolicyId(String((decidiu ?? cobre ?? q.data.policies[0])?.id ?? '') || null);
    }, [row, q.data]);

    const alvo = alvos[Number(alvoIdx)];
    const politica = politicas.find((p) => String(p.id) === policyId);
    const existente = alvo && politica?.rules.find((r) => r.type === alvo.type && r.target === alvo.target);

    const salvar = async () => {
        if (!alvo || !politica) return;
        setSalvando(true);
        try {
            await axios.post(
                `/api/firewall/policies/${politica.id}/rules`,
                { type: alvo.type, target: alvo.target, action: acao, schedule: null, source: 'manual' },
                { withCredentials: true },
            );
            toastSuccess(`Regra criada em "${politica.name}". ${AVISO_SYNC}`);
            onClose();
        } catch (err) {
            toastError(extractApiError(err, 'Erro ao criar a regra.').message);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            show={!!row}
            size="md"
            title="Nova regra a partir do acesso"
            onClose={onClose}
            onConfirm={salvar}
            confirmProps={{
                label: salvando ? 'Salvando…' : 'Criar regra',
                disabled: salvando || !alvo || !politica || !!existente,
            }}
        >
            <VBox gap={12}>
                <Carregando q={q} />
                {alvos.length === 0 && <AlertBox variant="warning">Esta linha não tem destino que vire regra.</AlertBox>}

                {alvos.length > 1 ? (
                    <div>
                        <strong style={{ fontSize: 14 }}>Alvo</strong>
                        <RadioGroup
                            value={alvoIdx}
                            onValueChange={(v) => setAlvoIdx(String(v))}
                            style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
                        >
                            {alvos.map((a, i) => (
                                <div key={`${a.type}:${a.target}`}>
                                    <CampoRadio
                                        id={`regra-alvo-${i}`}
                                        value={String(i)}
                                        label={a.label}
                                        checked={alvoIdx === String(i)}
                                        onChange={() => setAlvoIdx(String(i))}
                                    />
                                    {a.hint && alvoIdx === String(i) && (
                                        <div style={{ fontSize: 12, opacity: .7, marginLeft: 24 }}>{a.hint}</div>
                                    )}
                                </div>
                            ))}
                        </RadioGroup>
                    </div>
                ) : alvo && (
                    <div><strong style={{ fontSize: 14 }}>Alvo:</strong> {alvo.label}</div>
                )}

                <CampoSeletor
                    label="Ação"
                    value={acao}
                    onChange={(v) => setAcao(v === 'block' ? 'block' : 'allow')}
                    options={[
                        { id: 'allow', label: 'Permitir' },
                        { id: 'block', label: 'Bloquear' },
                    ]}
                    noSearch
                />

                {q.data && (politicas.length === 0 ? (
                    <AlertBox variant="warning">Nenhuma política {row?.type === 'proxy' ? 'de proxy' : 'de firewall'} se aplica a esta appliance.</AlertBox>
                ) : politicas.length === 1 ? (
                    <div><strong style={{ fontSize: 14 }}>Política:</strong> {politicas[0].name}</div>
                ) : (
                    <CampoSeletor
                        label="Política"
                        value={policyId}
                        onChange={setPolicyId}
                        options={politicas.map((p) => ({
                            id: String(p.id),
                            label: `${p.name}${p.id === row?.policyId ? ' — decidiu este acesso' : p.coversOrigin ? '' : ' — não vale para esta origem'}${p.enabled ? '' : ' (desativada)'}`,
                        }))}
                        noSearch={politicas.length <= 6}
                    />
                ))}

                {politica && !politica.coversOrigin && (
                    <AlertBox variant="warning">"{politica.name}" não vale para {row?.originIp}: a regra não muda este acesso.</AlertBox>
                )}
                {politica && !politica.enabled && (
                    <AlertBox variant="warning">"{politica.name}" está desativada.</AlertBox>
                )}
                {existente && (
                    <AlertBox variant="info">
                        Já existe esta regra ({existente.action === 'allow' ? 'permitir' : 'bloquear'}) em "{politica?.name}". Se o acesso continua igual, falta sincronizar ou outra regra antes dela decide primeiro.
                    </AlertBox>
                )}
                {politica && !existente && (
                    <span style={{ fontSize: 12, opacity: .7 }}>
                        A regra entra no fim da lista da política; regras anteriores que casem com o mesmo acesso decidem primeiro. {AVISO_SYNC}
                    </span>
                )}
            </VBox>
        </Modal>
    );
}

// ─── Grupo de dispositivos ────────────────────────────────────────────────────

export function GrupoDaLinhaModal({ row, onClose }: { row: AccessReportRowDTO | null; onClose: () => void }) {
    const q = useOpcoes(row);
    const [grupoId, setGrupoId] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const disponiveis = (q.data?.groups ?? []).filter((g) => !g.member);
    const membro = (q.data?.groups ?? []).filter((g) => g.member);

    useEffect(() => {
        setGrupoId(disponiveis.length === 1 ? String(disponiveis[0].id) : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q.data]);

    const salvar = async () => {
        const grupo = disponiveis.find((g) => String(g.id) === grupoId);
        if (!grupo || !q.data?.device) return;
        setSalvando(true);
        try {
            await axios.post(`/api/grupo_dispositivo/${grupo.id}/membros/${q.data.device.id}`, {}, { withCredentials: true });
            toastSuccess(`${q.data.device.name} incluído em "${grupo.name}". ${AVISO_SYNC}`);
            onClose();
        } catch (err) {
            toastError(extractApiError(err, 'Erro ao incluir no grupo.').message);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            show={!!row}
            size="sm"
            title="Incluir dispositivo em grupo"
            onClose={onClose}
            onConfirm={salvar}
            confirmProps={{ label: salvando ? 'Salvando…' : 'Incluir', disabled: salvando || !grupoId || !q.data?.device }}
        >
            <VBox gap={12}>
                <Carregando q={q} />
                {q.data && !q.data.device && (
                    <AlertBox variant="warning">{row?.originIp} não é um dispositivo cadastrado nesta appliance.</AlertBox>
                )}
                {q.data?.device && (
                    <>
                        <div><strong style={{ fontSize: 14 }}>Dispositivo:</strong> {q.data.device.name} ({row?.originIp})</div>
                        {membro.length > 0 && (
                            <span style={{ fontSize: 12, opacity: .7 }}>Já está em: {membro.map((g) => g.name).join(', ')}</span>
                        )}
                        {disponiveis.length === 0 ? (
                            <AlertBox variant="info">
                                {q.data.groups.length === 0 ? 'Esta appliance não tem grupos de dispositivos.' : 'O dispositivo já está em todos os grupos desta appliance.'}
                            </AlertBox>
                        ) : disponiveis.length === 1 ? (
                            <div><strong style={{ fontSize: 14 }}>Grupo:</strong> {disponiveis[0].name}</div>
                        ) : (
                            <CampoSeletor
                                label="Grupo"
                                value={grupoId}
                                onChange={setGrupoId}
                                options={disponiveis.map((g) => ({ id: String(g.id), label: g.name }))}
                                noSearch={disponiveis.length <= 6}
                            />
                        )}
                        <span style={{ fontSize: 12, opacity: .7 }}>{AVISO_SYNC}</span>
                    </>
                )}
            </VBox>
        </Modal>
    );
}

// ─── Cadastrar dispositivo ────────────────────────────────────────────────────

/**
 * Cadastra a origem da linha como dispositivo LAN. MAC e nome vêm da tabela ARP
 * (o mesmo `/discovered` da aba "Descobertos"); com o agente offline ou o IP
 * fora do ARP, o operador preenche à mão.
 */
export function DispositivoDaLinhaModal({ row, onClose }: { row: AccessReportRowDTO | null; onClose: () => void }) {
    const queryClient = useQueryClient();
    const applianceId = row?.applianceId;
    const [device, setDevice] = useState<NetworkLanDeviceDTO | null>(null);
    const [grupoIds, setGrupoIds] = useState<number[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [salvando, setSalvando] = useState(false);

    const arp = useQuery(
        ['arp_discovered', applianceId],
        async () => {
            const res = await axios.get<{ result: ArpDiscoveredDeviceDTO[] }>(
                `/api/lan_dispositivo/discovered?appliance_id=${applianceId}`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        { enabled: !!row && !!applianceId, staleTime: 0 },
    );
    const grupos = useQuery(
        ['grupo_dispositivo', applianceId],
        async () => {
            const res = await axios.get<{ result: DeviceGroupRefDTO[] }>(
                `/api/grupo_dispositivo?appliance_id=${applianceId}`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        { enabled: !!row && !!applianceId },
    );

    useEffect(() => {
        if (!row) { setDevice(null); return; }
        setGrupoIds([]);
        setFieldErrors({});
        setDevice({ nome: '', tipo: 'pc', mac: '', ip: row.originIp ?? '', appliance_id: row.applianceId! });
    }, [row]);

    // Completa MAC/nome quando o ARP chegar, sem sobrescrever o que já foi digitado.
    const achado = arp.data?.find((d) => d.ip === row?.originIp);
    useEffect(() => {
        if (!achado) return;
        setDevice((d) => d && ({ ...d, mac: d.mac || achado.mac, nome: d.nome || achado.netbios_name || '' }));
    }, [achado]);

    const salvar = async () => {
        if (!device) return;
        setSalvando(true);
        setFieldErrors({});
        try {
            const res = await axios.post<{ result?: { id?: number } }>('/api/lan_dispositivo', device, { withCredentials: true });
            const id = res.data.result?.id;
            if (id && grupoIds.length > 0) {
                await axios.put(`/api/lan_dispositivo/${id}/grupos`, { grupoIds }, { withCredentials: true });
            }
            toastSuccess(`${device.nome || device.ip} cadastrado. ${AVISO_SYNC}`);
            queryClient.invalidateQueries('access_logs');
            queryClient.invalidateQueries('arp_discovered');
            onClose();
        } catch (err) {
            const { message, fieldErrors: errs } = extractApiError(err, 'Erro ao cadastrar o dispositivo.');
            if (errs) setFieldErrors(errs);
            toastWarning(message);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            show={!!row && !!device}
            ignoreOverlayClick
            title="Cadastrar dispositivo"
            onClose={onClose}
            onCancel={onClose}
            onConfirm={salvar}
            confirmProps={{ label: salvando ? 'Salvando…' : 'Cadastrar', disabled: salvando }}
        >
            <VBox gap={12}>
                {arp.isLoading && <span style={{ opacity: .7 }}>Consultando a tabela ARP…</span>}
                {arp.isFetched && !achado && (
                    <AlertBox variant="info">{row?.originIp} não está na tabela ARP da appliance agora — informe o MAC manualmente.</AlertBox>
                )}
                {device && (
                    <FormLanDevice
                        value={device}
                        onChange={setDevice}
                        fieldErrors={fieldErrors}
                        grupoIds={grupoIds}
                        onGrupoIdsChange={setGrupoIds}
                        grupoOptions={(grupos.data ?? []).map((g) => ({ id: String(g.id), label: g.nome }))}
                    />
                )}
            </VBox>
        </Modal>
    );
}
